

// ===========================================
// Drop Stitch Canvas Animation - ES6 Class
// ===========================================
class DropStitchSimulation {
  constructor() {
    this.canvas = document.getElementById('dropStitchCanvas');
    this.slider = document.getElementById('pressure-slider');
    this.psiDisplay = document.getElementById('psi-display');
    this.currentMode = 'dropstitch';
    this.pressure = 0;
    this.ctx = null;
    this.animationId = null;
    this.time = 0;

    if (this.canvas) {
      this.init();
    }
  }

  init() {
    this.ctx = this.canvas.getContext('2d');
    this.setupCanvas();
    this.attachEventListeners();
    this.setupModeButtons();
    this.animate();
  }

  setupCanvas() {
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    const container = this.canvas.parentElement;
    // Get computed style to handle hidden elements
    const computedStyle = window.getComputedStyle(container);
    const width = parseInt(computedStyle.width) || 600;
    const height = parseInt(computedStyle.height) || 250;

    this.canvas.width = width;
    this.canvas.height = height;
  }

  attachEventListeners() {
    if (this.slider) {
      this.slider.addEventListener('input', (e) => this.handlePressureChange(e));
    }
  }

  setupModeButtons() {
    window.setSimulationMode = (mode) => this.setMode(mode);
  }

  handlePressureChange(event) {
    this.targetPressure = parseFloat(event.target.value);
    this.animatePressureDisplay(this.pressure, this.targetPressure);
    this.pressure = this.targetPressure;
  }

  animatePressureDisplay(from, to) {
    const duration = 300;
    const startTime = Date.now();
    const diff = to - from;

    const updateDisplay = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = from + (diff * easeOut);

      this.psiDisplay.textContent = `${current.toFixed(1)} PSI`;
      this.updatePsiDisplayStyle();

      if (progress < 1) {
        requestAnimationFrame(updateDisplay);
      }
    };

    updateDisplay();
  }

  updatePsiDisplayStyle() {
    if (this.currentMode === 'airbed') {
      if (this.pressure > 2) {
        this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--danger';
      } else {
        this.psiDisplay.className = 'technology-section__psi-display';
      }
    } else {
      if (this.pressure < 4) {
        this.psiDisplay.className = 'technology-section__psi-display';
      } else if (this.pressure < 8) {
        this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--warning';
      } else {
        this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--success';
      }
    }
  }

  setMode(mode) {
    this.currentMode = mode;
    this.pressure = 0;
    this.slider.value = 0;
    this.psiDisplay.textContent = '0 PSI';

    const btnAirbed = document.getElementById('btn-airbed');
    const btnDS = document.getElementById('btn-dropstitch');
    const maxLabel = document.getElementById('max-psi-label');
    const desc = document.getElementById('sim-desc');

    if (mode === 'airbed') {
      btnAirbed.className = 'nav-link active';
      btnDS.className = 'nav-link';
      this.slider.max = 3;
      maxLabel.textContent = '3 PSI (BURST RISK)';
      desc.innerHTML = '<span style="color: #dc2626; font-weight: bold;">Warning:</span> Without internal threads, increasing pressure causes severe deformation.';
    } else {
      btnDS.className = 'nav-link active';
      btnAirbed.className = 'nav-link';
      this.slider.max = 15;
      maxLabel.textContent = '15 PSI';
      desc.innerHTML = 'Internal threads hold the flat shape even at high pressure, creating a rigid and comfortable surface.';
    }
  }

  animate() {
    if (!this.ctx || !this.canvas) return;

    this.time += 0.016;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const width = this.canvas.width * 0.8;
    const maxThickness = 100;
    const minThickness = 40;
    let currentThickness;

    if (this.currentMode === 'dropstitch') {
      currentThickness = minThickness + (this.pressure / 15) * (maxThickness - minThickness);
    } else {
      currentThickness = minThickness + (this.pressure / 2) * (maxThickness - minThickness);
    }

    const topY = centerY - currentThickness / 2;
    const bottomY = centerY + currentThickness / 2;

    this.drawThreads(centerX, width, topY, bottomY);
    this.drawSurfaces(centerX, centerY, width, topY, bottomY);
    this.drawStatusText(centerX, centerY);

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  drawThreads(centerX, width, topY, bottomY) {
    if (this.currentMode !== 'dropstitch') return;

    const threadCount = 40;
    const threadSpacing = width / threadCount;
    const opacity = 0.3 + (this.pressure / 30);
    this.ctx.strokeStyle = 'rgba(200, 200, 200, ' + opacity + ')';
    this.ctx.lineWidth = 1;

    for (let i = 0; i <= threadCount; i++) {
      const x = (centerX - width/2) + (i * threadSpacing);
      const waviness = (15 - this.pressure) * 2;
      const controlX = x + (Math.sin(this.time * 5 + i) * waviness);

      this.ctx.beginPath();
      this.ctx.moveTo(x, topY);
      
      if (this.pressure < 5) {
        this.ctx.bezierCurveTo(controlX, topY + 20, controlX, bottomY - 20, x, bottomY);
      } else {
        this.ctx.lineTo(x, bottomY);
      }
      
      this.ctx.stroke();
    }
  }

  drawSurfaces(centerX, centerY, width, topY, bottomY) {
    this.ctx.lineWidth = 6;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#334155';
    
    this.ctx.beginPath();
    for (let x = centerX - width/2; x <= centerX + width/2; x += 10) {
      const distFromCenter = Math.abs(x - centerX);
      const normalizedDist = 1 - (distFromCenter / (width/2));
      let y;
      
      if (this.currentMode === 'dropstitch') {
        const sag = (15 - this.pressure) * 1.5 * Math.sin(normalizedDist * Math.PI);
        y = topY + sag;
      } else {
        const balloonHeight = (this.pressure * 25) * Math.sin(normalizedDist * Math.PI);
        y = topY - balloonHeight;
      }
      
      if (x === centerX - width/2) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
    
    this.ctx.beginPath();
    for (let x = centerX - width/2; x <= centerX + width/2; x += 10) {
      const distFromCenter = Math.abs(x - centerX);
      const normalizedDist = 1 - (distFromCenter / (width/2));
      let y;
      
      if (this.currentMode === 'dropstitch') {
        y = bottomY;
      } else {
        const balloonHeight = (this.pressure * 25) * Math.sin(normalizedDist * Math.PI);
        y = bottomY + balloonHeight;
      }
      
      if (x === centerX - width/2) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
  }

  drawStatusText(centerX, centerY) {
    this.ctx.textAlign = 'center';
    
    if (this.currentMode === 'dropstitch' && this.pressure > 10) {
      this.ctx.fillStyle = '#3d6b3d';
      this.ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
      this.ctx.fillText('RIGID & STABLE', centerX, centerY + 5);
    } else if (this.currentMode === 'airbed' && this.pressure > 2) {
      this.ctx.fillStyle = '#ef4444';
      this.ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';
      this.ctx.fillText('DEFORMED / UNSTABLE', centerX, centerY + 5);
    }
  }
}

// ===========================================
// Hollow Canvas Animation - ES6 Class
// ===========================================
class HollowSimulation {
  constructor() {
    this.canvas = document.getElementById('hollowCanvas');
    this.slider = document.getElementById('pressure-slider-hollow');
    this.psiDisplay = document.getElementById('psi-display-hollow');
    this.pressure = 0;
    this.ctx = null;
    this.animationId = null;
    this.time = 0;

    if (this.canvas) {
      this.init();
    }
  }

  init() {
    this.ctx = this.canvas.getContext('2d');
    this.setupCanvas();
    this.attachEventListeners();
    this.animate();
  }

  setupCanvas() {
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    const container = this.canvas.parentElement;
    // Get computed style to handle hidden elements
    const computedStyle = window.getComputedStyle(container);
    const width = parseInt(computedStyle.width) || 600;
    const height = parseInt(computedStyle.height) || 250;

    this.canvas.width = width;
    this.canvas.height = height;
  }

  attachEventListeners() {
    if (this.slider) {
      this.slider.addEventListener('input', (e) => this.handlePressureChange(e));
    }
  }

  handlePressureChange(event) {
    this.targetPressure = parseFloat(event.target.value);
    this.animatePressureDisplay(this.pressure, this.targetPressure);
    this.pressure = this.targetPressure;
  }

  animatePressureDisplay(from, to) {
    const duration = 300;
    const startTime = Date.now();
    const diff = to - from;

    const updateDisplay = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = from + (diff * easeOut);

      this.psiDisplay.textContent = current.toFixed(1) + ' PSI';
      this.updatePsiDisplayStyle();

      if (progress < 1) {
        requestAnimationFrame(updateDisplay);
      }
    };

    updateDisplay();
  }

  updatePsiDisplayStyle() {
    if (this.pressure >= 7) {
      this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--danger';
    } else if (this.pressure >= 4) {
      this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--warning';
    } else {
      this.psiDisplay.className = 'technology-section__psi-display';
    }
  }

  animate() {
    if (!this.ctx || !this.canvas) return;

    this.time += 0.016;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const width = this.canvas.width * 0.8;

    this.drawHollowStructure(centerX, centerY, width);

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  drawHollowStructure(centerX, centerY, width) {
    const minThickness = 40;
    const balloonFactor = Math.pow(this.pressure / 8, 1.5);
    const maxBalloon = 120;
    const currentBalloon = minThickness + (balloonFactor * maxBalloon);
    
    this.ctx.lineWidth = 6;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#334155';
    
    this.ctx.beginPath();
    for (let x = centerX - width/2; x <= centerX + width/2; x += 10) {
      const distFromCenter = Math.abs(x - centerX);
      const normalizedDist = distFromCenter / (width/2);
      const bulgeFactor = Math.sin((1 - normalizedDist) * Math.PI);
      const y = centerY - currentBalloon / 2 - (bulgeFactor * currentBalloon * 0.3);
      
      if (x === centerX - width/2) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
    
    this.ctx.beginPath();
    for (let x = centerX - width/2; x <= centerX + width/2; x += 10) {
      const distFromCenter = Math.abs(x - centerX);
      const normalizedDist = distFromCenter / (width/2);
      const bulgeFactor = Math.sin((1 - normalizedDist) * Math.PI);
      const y = centerY + currentBalloon / 2 + (bulgeFactor * currentBalloon * 0.3);
      
      if (x === centerX - width/2) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();
    
    this.ctx.textAlign = 'center';
    this.ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';

    if (this.pressure < 3) {
      this.ctx.fillStyle = '#64748b';
      this.ctx.fillText('DEFLATED', centerX, centerY + 5);
    } else if (this.pressure < 6) {
      this.ctx.fillStyle = '#233f23';
      this.ctx.fillText('BALLOONING', centerX, centerY + 5);
    } else {
      this.ctx.fillStyle = '#dc2626';
      this.ctx.fillText('DEFORMED / UNSTABLE', centerX, centerY + 5);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new DropStitchSimulation();
  new HollowSimulation();
  initTabSwitching();
});

// ===========================================
// Simple Tab Switching
// ===========================================
function initTabSwitching() {
  const tabButtons = document.querySelectorAll('.technology-section__tab-btn');
  const tabPanes = document.querySelectorAll('.technology-section__tab-pane');

  tabButtons.forEach(button => {
    button.addEventListener('click', () => {
      const targetTab = button.getAttribute('data-tab');

      tabButtons.forEach(btn => btn.classList.remove('technology-section__tab-btn--active'));
      tabPanes.forEach(pane => pane.classList.remove('technology-section__tab-pane--active'));

      button.classList.add('technology-section__tab-btn--active');
      const targetPane = document.querySelector(`[data-pane="${targetTab}"]`);
      if (targetPane) {
        targetPane.classList.add('technology-section__tab-pane--active');

        // Trigger resize event to update canvas sizes
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 10);
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  new DropStitchSimulation();
  new HollowSimulation();
  initTabSwitching();
});

// ===========================================
// Drop Stitch V2 - Enhanced Animation
// ===========================================
class DropStitchV2 {
  constructor() {
    this.canvas = document.getElementById('dropStitchCanvasV2');
    this.slider = document.getElementById('pressure-slider-v2');
    this.psiDisplay = document.getElementById('psi-display-v2');
    this.pressure = 0;
    this.ctx = null;
    this.animationId = null;
    this.time = 0;
    this.particles = [];

    if (this.canvas) {
      this.init();
    }
  }

  init() {
    this.ctx = this.canvas.getContext('2d');
    this.setupCanvas();
    this.attachEventListeners();
    this.initParticles();
    this.animate();
  }

  setupCanvas() {
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
  }

  resizeCanvas() {
    const container = this.canvas.parentElement;
    const computedStyle = window.getComputedStyle(container);
    const width = parseInt(computedStyle.width) || 600;
    const height = parseInt(computedStyle.height) || 250;

    this.canvas.width = width;
    this.canvas.height = height;
  }

  attachEventListeners() {
    if (this.slider) {
      this.slider.addEventListener('input', (e) => this.handlePressureChange(e));
    }
  }

  handlePressureChange(event) {
    this.targetPressure = parseFloat(event.target.value);
    this.animatePressureDisplay(this.pressure, this.targetPressure);
    this.pressure = this.targetPressure;
  }

  animatePressureDisplay(from, to) {
    const duration = 300;
    const startTime = Date.now();
    const diff = to - from;

    const updateDisplay = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = from + (diff * easeOut);

      this.psiDisplay.textContent = current.toFixed(1) + ' PSI';
      this.updatePsiDisplayStyle();

      if (progress < 1) {
        requestAnimationFrame(updateDisplay);
      }
    };

    updateDisplay();
  }

  updatePsiDisplayStyle() {
    if (this.pressure < 5) {
      this.psiDisplay.className = 'technology-section__psi-display';
    } else if (this.pressure < 10) {
      this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--warning';
    } else {
      this.psiDisplay.className = 'technology-section__psi-display technology-section__psi-display--success';
    }
  }

  initParticles() {
    this.particles = [];
    for (let i = 0; i < 30; i++) {
      this.particles.push({
        x: Math.random(),
        y: Math.random(),
        speed: 0.0005 + Math.random() * 0.001,
        size: 1 + Math.random() * 2
      });
    }
  }

  animate() {
    if (!this.ctx || !this.canvas) return;

    this.time += 0.016;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const width = this.canvas.width * 0.75;
    const maxThickness = 100;
    const minThickness = 40;

    const currentThickness = minThickness + (this.pressure / 15) * (maxThickness - minThickness);
    const topY = centerY - currentThickness / 2;
    const bottomY = centerY + currentThickness / 2;

    this.drawBackground();
    this.drawParticles();
    this.drawThreadsV2(centerX, width, topY, bottomY);
    this.drawSurfacesV2(centerX, centerY, width, topY, bottomY);
    this.drawPressureIndicators(centerX, centerY, width);
    this.drawStatusText(centerX, centerY);

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  drawBackground() {
    const gradient = this.ctx.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
    gradient.addColorStop(0, 'rgba(248, 250, 252, 0.5)');
    gradient.addColorStop(1, 'rgba(241, 245, 249, 0.5)');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  drawParticles() {
    this.particles.forEach(particle => {
      particle.y += particle.speed;
      if (particle.y > 1) particle.y = 0;

      const x = particle.x * this.canvas.width;
      const y = particle.y * this.canvas.height;
      const alpha = 0.1 + (this.pressure / 15) * 0.3;

      this.ctx.fillStyle = 'rgba(35, 63, 35, ' + alpha + ')';
      this.ctx.beginPath();
      this.ctx.arc(x, y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
    });
  }

  drawThreadsV2(centerX, width, topY, bottomY) {
    const threadCount = 50;
    const threadSpacing = width / threadCount;

    for (let i = 0; i <= threadCount; i++) {
      const x = (centerX - width/2) + (i * threadSpacing);
      const tension = Math.max(0, 15 - this.pressure);
      const waviness = tension * 1.2;
      const phase = this.time * 3 + i * 0.2;
      const controlX = x + (Math.sin(phase) * waviness);

      const opacity = 0.2 + (this.pressure / 20);
      const hue = 200 + (this.pressure * 2);
      this.ctx.strokeStyle = 'hsla(' + hue + ', 50%, 60%, ' + opacity + ')';
      this.ctx.lineWidth = 1.5 + (this.pressure / 15);

      this.ctx.beginPath();
      this.ctx.moveTo(x, topY);

      if (this.pressure < 5) {
        this.ctx.bezierCurveTo(controlX, topY + 20, controlX, bottomY - 20, x, bottomY);
      } else {
        this.ctx.lineTo(x, bottomY);
      }

      this.ctx.stroke();

      if (i % 5 === 0 && this.pressure > 8) {
        this.ctx.fillStyle = 'rgba(35, 63, 35, 0.6)';
        this.ctx.beginPath();
        this.ctx.arc(x, topY, 2, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.beginPath();
        this.ctx.arc(x, bottomY, 2, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }

  drawSurfacesV2(centerX, centerY, width, topY, bottomY) {
    this.ctx.lineWidth = 6;
    this.ctx.lineCap = 'round';
    
    const gradient = this.ctx.createLinearGradient(centerX - width/2, 0, centerX + width/2, 0);
    gradient.addColorStop(0, '#1e293b');
    gradient.addColorStop(0.5, '#334155');
    gradient.addColorStop(1, '#1e293b');
    this.ctx.strokeStyle = gradient;

    this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    this.ctx.shadowBlur = 5;
    this.ctx.shadowOffsetY = 2;

    this.ctx.beginPath();
    for (let x = centerX - width/2; x <= centerX + width/2; x += 8) {
      const distFromCenter = Math.abs(x - centerX);
      const normalizedDist = 1 - (distFromCenter / (width/2));
      const sag = (15 - this.pressure) * 1.2 * Math.sin(normalizedDist * Math.PI);
      const y = topY + sag;

      if (x === centerX - width/2) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.moveTo(centerX - width/2, bottomY);
    this.ctx.lineTo(centerX + width/2, bottomY);
    this.ctx.stroke();

    this.ctx.shadowColor = 'transparent';
    this.ctx.shadowBlur = 0;
  }

  drawPressureIndicators(centerX, centerY, width) {
    if (this.pressure < 3) return;

    const indicatorCount = Math.floor(this.pressure / 3);
    const spacing = 30;

    for (let i = 0; i < indicatorCount; i++) {
      const x = centerX - width/2 - 20;
      const y = centerY - (indicatorCount * spacing / 2) + (i * spacing);

      this.ctx.fillStyle = 'rgba(35, 63, 35, 0.7)';
      this.ctx.beginPath();
      this.ctx.arc(x, y, 4, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.strokeStyle = 'rgba(35, 63, 35, 0.3)';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(x, y, 8 + Math.sin(this.time * 5 + i) * 2, 0, Math.PI * 2);
      this.ctx.stroke();
    }
  }

  drawStatusText(centerX, centerY) {
    this.ctx.textAlign = 'center';
    this.ctx.font = 'bold 14px system-ui, -apple-system, sans-serif';

    if (this.pressure > 12) {
      this.ctx.fillStyle = '#16a34a';
      this.ctx.fillText('MAXIMUM RIGIDITY', centerX, centerY + 5);
    } else if (this.pressure > 8) {
      this.ctx.fillStyle = '#3d6b3d';
      this.ctx.fillText('OPTIMAL PRESSURE', centerX, centerY + 5);
    } else if (this.pressure > 4) {
      this.ctx.fillStyle = '#233f23';
      this.ctx.fillText('STABILIZING', centerX, centerY + 5);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new DropStitchSimulation();
  new HollowSimulation();
  new DropStitchV2();
  initTabSwitching();
});


/**
 * Generic tab switching functionality
 * @param {Object} options - Configuration options
 * @param {string} options.tabSelector - CSS selector for tab buttons
 * @param {string} options.paneSelector - CSS selector for tab panes
 * @param {string} options.activeTabClass - Class name for active tab button
 * @param {string} options.activePaneClass - Class name for active pane
 * @param {string} options.tabAttribute - Attribute name for tab identifier (default: 'data-tab')
 * @param {string} options.paneAttribute - Attribute name for pane identifier (default: 'data-pane')
 * @param {Function} options.onTabChange - Optional callback after tab change
 */
function initTabSwitching2(options) {
  const {
    tabSelector,
    paneSelector,
    activeTabClass,
    activePaneClass,
    tabAttribute = "data-tab",
    paneAttribute = "data-pane",
    onTabChange,
    enableAnimation = false,
  } = options;

  const tabButtons = document.querySelectorAll(tabSelector);
  const tabPanes = document.querySelectorAll(paneSelector);

  if (tabButtons.length === 0 || tabPanes.length === 0) return;

  tabButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const targetTab = button.getAttribute(tabAttribute);
      const currentActivePane = document.querySelector(
        `${paneSelector}.${activePaneClass}`,
      );
      const targetPane = document.querySelector(
        `[${paneAttribute}="${targetTab}"]`,
      );

      // Skip if clicking the already active tab
      if (currentActivePane === targetPane) return;

      // Update tab buttons
      tabButtons.forEach((btn) => btn.classList.remove(activeTabClass));
      button.classList.add(activeTabClass);

      if (enableAnimation && currentActivePane && targetPane) {
        // Add fade-out class to current pane
        currentActivePane.classList.add("fade-out");

        // Wait for fade-out animation to complete
        setTimeout(() => {
          // Remove active class from current pane
          currentActivePane.classList.remove(activePaneClass, "fade-out");

          // Add active class and fade-in to target pane
          targetPane.classList.add(activePaneClass, "fade-in");

          // Remove fade-in class after animation completes
          setTimeout(() => {
            targetPane.classList.remove("fade-in");
          }, 300);

          if (onTabChange) {
            onTabChange(targetTab, targetPane);
          }
        }, 300);
      } else {
        // No animation - instant switch
        tabPanes.forEach((pane) => pane.classList.remove(activePaneClass));
        if (targetPane) {
          targetPane.classList.add(activePaneClass);

          if (onTabChange) {
            onTabChange(targetTab, targetPane);
          }
        }
      }
    });
  });
}

// Initialize mr-gallery-v2 tabs with animation
initTabSwitching2({
  tabSelector: ".mr-gallery-v2__tab-btn",
  paneSelector: ".mr-gallery-v2__tab-pane",
  activeTabClass: "mr-gallery-v2__tab-btn--active",
  activePaneClass: "mr-gallery-v2__tab-pane--active",
  enableAnimation: true,
});




// Noise Chart
const noiseCtx = document.getElementById("noiseChart");
if (noiseCtx) {
  new Chart(noiseCtx, {
    type: "bar",
    data: {
      labels: ["MilesRunner", "Soft Topper", "Fiberglass", "Aluminium"],
      datasets: [
        {
          label: "Noise Level (dBA)",
          data: [45, 38, 72, 65],
          backgroundColor: ["#233f23", "#c720d5", "#6bacf7", "#68748b"],
          borderWidth: 0,
          borderRadius: 8,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          max: 80,
          ticks: {
            callback: function (value) {
              return value + " dBA";
            },
            font: { size: 10, color: "#cbd5e1" },
          },
          grid: { color: "rgba(203, 213, 225, 0.1)" },
        },
        x: {
          ticks: { font: { size: 10, color: "#cbd5e1" } },
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => ctx.raw + " dBA",
          },
        },
      },
    },
  });
}
console.log(noiseCtx);

// Temperature Stability Chart
const tempCtx = document.getElementById("tempChart");
if (tempCtx) {
  new Chart(tempCtx, {
    type: "line",
    data: {
      labels: ["10 PM", "12 AM", "2 AM", "4 AM", "6 AM", "8 AM"],
      datasets: [
        {
          label: "MilesRunner",
          data: [72, 72, 71, 71, 72, 72],
          borderColor: "#233f23",
          backgroundColor: "rgba(35, 63, 35, 0.2)",
          fill: true,
          tension: 0.4,
          borderWidth: 3,
        },
        {
          label: "Soft Topper",
          data: [72, 76, 78, 79, 78, 76],
          borderColor: "#c720d5",
          backgroundColor: "rgba(199, 32, 213, 0.2)",
          fill: true,
          tension: 0.4,
          borderWidth: 3,
        },
        {
          label: "Fiberglass",
          data: [72, 68, 65, 64, 65, 68],
          borderColor: "#6bacf7",
          backgroundColor: "rgba(107, 172, 247, 0.2)",
          fill: true,
          tension: 0.4,
          borderWidth: 3,
        },
        {
          label: "Aluminium",
          data: [72, 72, 71, 70, 71, 72],
          borderColor: "#68748b",
          backgroundColor: "rgba(104, 116, 139, 0.2)",
          fill: true,
          tension: 0.4,
          borderWidth: 3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          min: 60,
          max: 85,
          backgroundColor: "#f2f2f2",
          grid: {
            color: "rgba(0, 0, 0, 0.05)",
            drawBorder: false,
          },
          ticks: {
            callback: function (value) {
              return value + "°F";
            },
            font: { size: 10 },
            color: "#475569",
          },
          title: {
            display: true,
            text: "Temperature (°F)",
            font: { size: 11 },
            color: "#475569",
          },
        },
        x: {
          grid: {
            color: "rgba(0, 0, 0, 0.05)",
            drawBorder: false,
          },
          ticks: {
            font: { size: 10 },
            color: "#475569",
          },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            font: { size: 10 },
            padding: 8,
            usePointStyle: true,
            color: "#475569",
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}°F`,
          },
        },
      },
    },
  });
}
console.log(tempCtx);

// Radar Chart
const radarCtx = document.getElementById("radarChart");
if (radarCtx) {
  new Chart(radarCtx, {
    type: "radar",
    data: {
      labels: ["Durability", "Comfort", "Noise", "Temperature", "Setup Time"],
      datasets: [
        {
          label: "MilesRunner",
          data: [95, 90, 85, 88, 80],
          backgroundColor: "rgba(35, 63, 35, 0.2)",
          borderColor: "#233f23",
          borderWidth: 2,
          pointBackgroundColor: "#233f23",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "#233f23",
        },
        {
          label: "Soft Topper",
          data: [75, 95, 90, 60, 70],
          backgroundColor: "rgba(199, 32, 213, 0.2)",
          borderColor: "#c720d5",
          borderWidth: 2,
          pointBackgroundColor: "#c720d5",
        },
        {
          label: "Fiberglass",
          data: [30, 50, 40, 65, 85],
          backgroundColor: "rgba(107, 172, 247, 0.2)",
          borderColor: "#6bacf7",
          borderWidth: 2,
          pointBackgroundColor: "#6bacf7",
        },
        {
          label: "Aluminium",
          data: [60, 40, 50, 70, 90],
          backgroundColor: "rgba(104, 116, 139, 0.2)",
          borderColor: "#68748b",
          borderWidth: 2,
          pointBackgroundColor: "#68748b",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          ticks: {
            display: false,
          },
          pointLabels: {
            font: { size: 11, color: "#cbd5e1" },
          },
          grid: { color: "rgba(203, 213, 225, 0.2)" },
          angleLines: { color: "rgba(203, 213, 225, 0.2)" },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            font: { size: 11 },
            color: "#cbd5e1",
            padding: 15,
            usePointStyle: true,
          },
        },
      },
    },
  });
}


